import { createContext, createElement, useContext, type ComponentType, type MouseEventHandler, type ReactNode } from 'react';

/**
 * Router-agnostic links. packages/ui does not depend on a router: the app passes its router's
 * link component (adapted to these props) once to `AppShell`, and every composite uses it.
 */
export interface ShellLinkProps {
  href: string;
  className?: string;
  children: ReactNode;
  'aria-current'?: 'page';
  'aria-label'?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}

export type LinkComponent = ComponentType<ShellLinkProps>;

function PlainLink({ href, children, ...props }: ShellLinkProps) {
  return (
    <a href={href} {...props}>
      {children}
    </a>
  );
}

const LinkContext = createContext<LinkComponent>(PlainLink);

export function LinkProvider({ component, children }: { component: LinkComponent; children: ReactNode }) {
  return <LinkContext.Provider value={component}>{children}</LinkContext.Provider>;
}

export function useLinkComponent(): LinkComponent {
  return useContext(LinkContext);
}

/**
 * Adapts a router link that takes `to` (react-router's `Link`, …) to `LinkComponent`.
 * Call once at module level: `const AppLink = adaptRouterLink(Link);`.
 */
export function adaptRouterLink(RouterLink: ComponentType<Omit<ShellLinkProps, 'href'> & { to: string }>): LinkComponent {
  function AdaptedLink({ href, ...props }: ShellLinkProps) {
    return <RouterLink to={href} {...props} />;
  }
  return AdaptedLink;
}

/** Renders the app's router link (from `LinkProvider`). Use this in composites. */
export function ShellLink(props: ShellLinkProps) {
  // createElement: the component comes from context and is stable per app.
  return createElement(useContext(LinkContext), props);
}
